import { useEffect, useState } from 'preact/hooks';
import type { PublicFeedSummary } from '../api/types';
import { subscribeStream } from './eventStream';

/**
 * Live public catalog-activity feed (ADR-0029). Every visitor - anonymous
 * included - sees new works / guides arriving in near-real-time over the shared
 * SSE stream. These events are EPHEMERAL: there is no backfill and nothing is
 * persisted per viewer; the hook simply surfaces the most recent coalesced
 * summary the server pushed. `seq` increments on each arrival so a consumer can
 * react to a fresh batch even if its contents repeat.
 */
export interface PublicFeedState {
    latest: PublicFeedSummary | null;
    seq: number;
}

let cached: PublicFeedState = { latest: null, seq: 0 };
let unsubscribe: (() => void) | null = null;
const listeners = new Set<(state: PublicFeedState) => void>();

function publish(next: PublicFeedState): void {
    cached = next;
    listeners.forEach((l) => l(next));
}

function onPublic(data: string): void {
    try {
        const summary = JSON.parse(data) as PublicFeedSummary;
        publish({ latest: summary, seq: cached.seq + 1 });
    } catch {
        // Ignore a malformed frame rather than crashing the feed.
    }
}

export function usePublicFeed(): PublicFeedState {
    const [state, setState] = useState<PublicFeedState>(cached);

    useEffect(() => {
        listeners.add(setState);
        if (listeners.size === 1) {
            unsubscribe = subscribeStream('public', onPublic);
        } else if (state !== cached) {
            setState(cached);
        }
        return () => {
            listeners.delete(setState);
            if (listeners.size === 0 && unsubscribe) {
                unsubscribe();
                unsubscribe = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return state;
}
